/**
 * Auffangnetz für Fehler beim Aufbau der Oberfläche.
 *
 * Ohne das führt ein einziger Fehler in irgendeiner Komponente zu einer
 * weißen Seite: React baut den ganzen Baum ab und schreibt die Ursache nur
 * in die Entwicklerkonsole, die auf einem Handy niemand öffnet. Der Prüfer
 * steht dann auf dem Platz vor einem leeren Bildschirm und weiß nicht
 * einmal, ob seine Eingaben noch da sind.
 *
 * Deshalb hier: die Meldung im Klartext, die Zusicherung zu den
 * gespeicherten Daten, und drei Wege weiter. Die Fehlermeldung wird
 * angezeigt und nicht versteckt — sie ist das Einzige, womit sich der
 * Fehler später finden lässt.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

type Zustand = { fehler: Error | null; stelle: string | null }

export class Absturznetz extends Component<{ children: ReactNode }, Zustand> {
  state: Zustand = { fehler: null, stelle: null }

  static getDerivedStateFromError(fehler: Error): Partial<Zustand> {
    return { fehler }
  }

  componentDidCatch(fehler: Error, info: ErrorInfo) {
    // In die Konsole, damit es beim Nachsehen am Rechner auffindbar ist.
    console.error('Absturz in der Oberfläche:', fehler, info.componentStack)
    this.setState({ stelle: info.componentStack ?? null })
  }

  render() {
    if (!this.state.fehler) return this.props.children

    const text = `${this.state.fehler.name}: ${this.state.fehler.message}`

    return (
      <div className="mx-auto max-w-2xl p-4">
        <div className="karte border-stufe-3">
          <h1 className="text-xl font-bold text-stufe-3">
            Hier ist etwas schiefgegangen
          </h1>

          <p className="mt-2">
            Die Anwendung konnte diese Seite nicht aufbauen. Das liegt an einem
            Fehler im Programm, nicht an Ihrer Eingabe.
          </p>

          <p className="mt-2 font-semibold">
            Ihre Eingaben sind nicht verloren. Was auf dem Platz erfasst wurde,
            liegt auf dem Gerät und geht beim nächsten Senden hinaus — auch
            wenn Sie jetzt neu laden.
          </p>

          <pre className="mt-3 overflow-x-auto rounded-feld bg-slate-100 p-3 text-base">
            {text}
          </pre>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="knopf-haupt"
              onClick={() => window.location.reload()}
            >
              Seite neu laden
            </button>
            <a href="/" className="knopf-zweit">
              Zur Übersicht
            </a>
            <button
              type="button"
              className="knopf-still"
              onClick={() => {
                void navigator.clipboard?.writeText(
                  `${text}\n\n${this.state.stelle ?? ''}`,
                )
              }}
            >
              Fehler kopieren
            </button>
          </div>

          <p className="mt-3 text-base text-slate-600">
            „Fehler kopieren“ legt die Meldung in die Zwischenablage — damit
            lässt sich die Ursache finden, ohne dass Sie sie abtippen.
          </p>
        </div>
      </div>
    )
  }
}
